import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import {Box, Button, Typography} from '@mui/material';

import {DepartmentHealthProfileDecisionGuideMostUrgentExampleCard} from './DepartmentHealthProfileDecisionGuideMostUrgentExampleCard';
import {DepartmentHealthProfileDecisionGuideSkeleton} from './DepartmentHealthProfileDecisionGuideSkeleton';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {
  accent,
  departmentHealthActionType,
  redeploymentChip,
  scorePill,
} from '../../../../../layouts/theme/designSystem';
import {AppConfig} from '../../../../../lib/AppConfig';
import {scorePillChipSx} from '../../../../../utils/getScoreLevelColors';
import type {DepartmentHealthProfileDecisionGuide as ProfileDecisionGuide} from '../../types/DepartmentHealthTypes';
import {buildDepartmentHealthProfileDecisionGuideContext} from '../../utils/DepartmentHealth/buildDepartmentHealthProfileDecisionGuideContext';
import {getDepartmentHealthProfilePathChipStyles} from '../../utils/DepartmentHealth/getDepartmentHealthProfilePathChipStyles';
import {buildWorkforceProfilesListFilterUrl} from '../../utils/WorkforceProfiles/buildWorkforceProfilesListFilterUrl';

export interface Props {
  profileDecisionGuide?: ProfileDecisionGuide;
  loading?: boolean;
  embedded?: boolean;
}

const cardSx = {
  border: '0.5px solid',
  borderColor: 'divider',
  borderRadius: '14px',
  px: 2.5,
  py: 2.25,
  mb: 2,
} as const;

const sectionTitleSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  fontSize: '13px',
  fontWeight: 600,
  color: 'text.primary',
  mb: 0.75,
} as const;

const developPathStyles = departmentHealthActionType.developmentPlans;
const redeployPathStyles = redeploymentChip;

const getProfileDetailPath = (profileId: string) =>
  `${AppConfig.COMPANY_WORKFORCE_ANALYSIS_PROFILES_PATH}/${profileId}`;

const newTabLinkProps = {
  target: '_blank',
  rel: 'noopener noreferrer',
} as const;

export const DepartmentHealthProfileDecisionGuide = ({
  profileDecisionGuide,
  loading = false,
  embedded = false,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.profileDecisionGuide'});

  const context = useMemo(
    () => (profileDecisionGuide ? buildDepartmentHealthProfileDecisionGuideContext(profileDecisionGuide) : null),
    [profileDecisionGuide]
  );

  if (loading || !context) {
    return <DepartmentHealthProfileDecisionGuideSkeleton embedded={embedded} />;
  }

  const resolvedCardSx = embedded ? {...cardSx, mb: 0} : cardSx;

  const {belowThresholdProfiles, developPathCount, redeployPathCount, mostUrgentProfile} = context;
  const reviewAllHref = buildWorkforceProfilesListFilterUrl(belowThresholdProfiles.map((profile) => profile.profileId));

  return (
    <EmpionPaper sx={resolvedCardSx} data-testid='department-health-profile-decision-guide'>
      <Box component='h2' sx={sectionTitleSx}>
        <Box component='span' sx={{color: 'text.secondary', display: 'flex', alignItems: 'center'}}>
          <PeopleOutlinedIcon sx={{fontSize: 15}} />
        </Box>
        {t('title')}
      </Box>

      <Typography variant='body2' sx={{fontSize: '11.5px', color: 'text.secondary', mb: 1.75}}>
        {t('subtitle')}
      </Typography>

      {belowThresholdProfiles.length === 0 ? (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, py: 1, color: scorePill.high.color}}>
          <CheckCircleOutlineIcon sx={{fontSize: 18}} />
          <Typography variant='body2' sx={{fontSize: '13px'}}>
            {t('allClear')}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 1, mb: 2}}>
            <Box
              sx={{
                px: 1.75,
                py: 1.5,
                borderRadius: '10px',
                bgcolor: developPathStyles.iconBg,
                ...scorePillChipSx,
              }}
            >
              <Typography sx={{fontSize: '22px', fontWeight: 700, color: developPathStyles.iconColor, lineHeight: 1}}>
                {developPathCount}
              </Typography>
              <Typography sx={{fontSize: '11px', fontWeight: 600, color: developPathStyles.iconColor, mt: 0.375}}>
                {t('paths.develop.title')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: developPathStyles.iconColor,
                  opacity: 0.75,
                  mt: 0.25,
                  lineHeight: 1.4,
                }}
              >
                {t('paths.develop.description')}
              </Typography>
            </Box>

            <Box
              sx={{
                px: 1.75,
                py: 1.5,
                borderRadius: '10px',
                bgcolor: redeployPathStyles.bgcolor,
                ...scorePillChipSx,
              }}
            >
              <Typography sx={{fontSize: '22px', fontWeight: 700, color: redeployPathStyles.color, lineHeight: 1}}>
                {redeployPathCount}
              </Typography>
              <Typography sx={{fontSize: '11px', fontWeight: 600, color: redeployPathStyles.color, mt: 0.375}}>
                {t('paths.redeploy.title')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: redeployPathStyles.color,
                  opacity: 0.75,
                  mt: 0.25,
                  lineHeight: 1.4,
                }}
              >
                {t('paths.redeploy.description')}
              </Typography>
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: '10.5px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: 'text.secondary',
              mb: 0.875,
            }}
          >
            {t('chipsHeading')}
          </Typography>

          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.625, mb: 1.75}}>
            {belowThresholdProfiles.map((profile) => {
              const pathStyles = getDepartmentHealthProfilePathChipStyles(profile);
              const scoreLabel =
                profile.targetProfileAlignmentScore !== null ? `${profile.targetProfileAlignmentScore}%` : '—';

              return (
                <Box
                  key={profile.profileId}
                  component='a'
                  href={getProfileDetailPath(profile.profileId)}
                  {...newTabLinkProps}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.625,
                    px: 1.125,
                    py: 0.375,
                    borderRadius: '6px',
                    fontSize: '11.5px',
                    fontWeight: 500,
                    bgcolor: pathStyles.bgcolor,
                    color: pathStyles.color,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    textDecoration: 'none',
                    transition: 'opacity 0.12s',
                    ...scorePillChipSx,
                    '&:hover': {opacity: 0.85},
                  }}
                >
                  {profile.publicId}
                  <Box component='span' sx={{opacity: 0.65}}>
                    {scoreLabel}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {mostUrgentProfile && (
            <>
              <Typography
                sx={{
                  fontSize: '10.5px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'text.secondary',
                  mb: 0.875,
                }}
              >
                {t('exampleHeading')}
              </Typography>

              <DepartmentHealthProfileDecisionGuideMostUrgentExampleCard profile={mostUrgentProfile} />
            </>
          )}

          <Button
            fullWidth
            variant='outlined'
            component='a'
            href={reviewAllHref}
            {...newTabLinkProps}
            startIcon={<ArrowForwardOutlinedIcon sx={{fontSize: 13}} />}
            sx={{
              mt: 1.25,
              py: 1,
              borderRadius: '8px',
              fontSize: '12.5px',
              fontWeight: 500,
              borderColor: 'divider',
              bgcolor: accent.subtle,
              color: 'text.secondary',
              textTransform: 'none',
              '&:hover': {
                bgcolor: accent.subtle,
                color: 'text.primary',
                borderColor: 'divider',
              },
            }}
          >
            {t('reviewAll', {count: belowThresholdProfiles.length})}
          </Button>
        </>
      )}
    </EmpionPaper>
  );
};
